import { Divider, HtmlTrans, SectionHeader, Typography } from 'web-library';
import React from 'react';
import { TFunction, useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import { Theme } from '@mui/material';
import { UploadedFile } from 'web-library/models/UploadedFile';
import UploadSupportingDocumentPassportPhoto from './UploadSupportingDocumentPassportPhoto';
import { NonNationalExamType, UploadDocumentType } from '../../../../api/candidate/config/model';

const useStyles = makeStyles()((theme) => ({
  uploadDocumentStatement: {
    marginBottom: theme.spacing(1),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  sectionContent: {
    marginBottom: theme.spacing(2),
  },
  SPERSTitleContent: {
    marginBottom: theme.spacing(4),
  },
  list: {
    paddingLeft: '1.5rem',
  },
  lastList: {
    marginBottom: theme.spacing(4),
  },
  inputLabel: {
    marginBottom: theme.spacing(1),
    color: theme.color.text.textHeader,
  },
  divider: {
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(2),
  },
}));

const myPassportContentAEISSPERS = (
  classes: Record<string, string>,
  t: TFunction<'translation'>
) => (
  <>
    <Typography className={classes.sectionContent} variant="body1">
      {t('exam->>UploadDocument.myPassportContent')}
    </Typography>
    <Typography variant="body1">{t('exam->>UploadDocument.Note')}</Typography>
    <ul className={classes.list}>
      <li>
        <Typography variant="body1">{t('exam->>UploadDocument.myPassportContentList1')}</Typography>
      </li>
      <li>
        <Typography variant="body1">{t('exam->>UploadDocument.myPassportContentList2')}</Typography>
      </li>
      <li>
        <Typography className={classes.lastList} variant="body1">
          {t('exam->>UploadDocument.myPassportContentList3')}
        </Typography>
      </li>
    </ul>
  </>
);

interface NonNationalUpperUploadContentProps {
  isAEIS: boolean;
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  applicationType: NonNationalExamType;
  draftFiles: UploadedFile[];
  setDraftFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  isLoading?: boolean;
  isFetchedDraft: boolean;
  hasEditAccess: boolean;
  isPassportDisable: boolean;
}

const NonNationalUpperUploadContent = ({
  isAEIS,
  files,
  setFiles,
  applicationType,
  draftFiles,
  setDraftFiles,
  isLoading,
  isFetchedDraft,
  hasEditAccess,
  isPassportDisable,
}: NonNationalUpperUploadContentProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  return (
    <>
      {isAEIS ? (
        <>
          <SectionHeader title={t('exam->>UploadDocument.UploadSupportingDocuments')} />
          <Typography className={classes.sectionTitle} variant="h3">
            <HtmlTrans i18nKey="exam->>UploadDocument.AllRequiredDocumentsMustBeUploadedToBeProcessed" />
          </Typography>
          <br />
          <Typography className={classes.uploadDocumentStatement} variant="body1">
            <b>{t('exam->>UploadDocument.PleaseReadBeforeSubmittingYourDocuments')}</b>
          </Typography>
          <ul className={classes.list}>
            <li>
              <Typography variant="body1">
                <b>
                  {t('exam->>UploadDocument.TheDocumentsSubmittedAreFinalNoChangesOrReplacement')}
                </b>
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                {t('exam->>UploadDocument.PleaseNameTheDocumentsInEnglishLanguageOnly')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                {t('exam->>UploadDocument.ForDocumentsInOtherLanguages')}
              </Typography>
            </li>
            <li>
              <Typography
                variant="body1"
                dangerouslySetInnerHTML={{
                  __html: t(
                    'exam->>UploadDocument.PleaseEnsureAllUploadedDocumentsAreNotPasswordProtected'
                  ),
                }}
              />
            </li>
            <li>
              <Typography variant="body1">
                {t('exam->>UploadDocument.IncompleteSubmissionsWillNotBeProcessed')}
              </Typography>
            </li>
          </ul>
          <Typography className={classes.uploadDocumentStatement} variant="body1">
            <b>{t('exam->>UploadDocument.DocumentsToUpload')}</b>
          </Typography>
          <ol className={classes.list}>
            <li>
              <Typography variant="body1">
                {t('exam->>UploadDocument.ApplicantBirthCertificate')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                {t('exam->>UploadDocument.ApplicantPassPortBioDataPage')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                {t('exam->>UploadDocument.PrimarySchoolApplicantsOnly')}
              </Typography>
              <Typography variant="body1">
                <b>{t('exam->>UploadDocument.OnlyTheSetOfCEQTestResultsWasSubmitted')}</b>
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                {t('exam->>UploadDocument.ApplicantStudentPassAndOrDependentPass')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                {t('exam->>UploadDocument.SingaporeCitizenParentsOnly')}
              </Typography>
            </li>
            <li>
              <Typography variant="body1">
                {t(
                  'exam->>UploadDocument.PleaseNoteThatAdditionalDocumentsMayBeRequiredForReviewIfNecessary'
                )}
              </Typography>
            </li>
          </ol>
          <UploadSupportingDocumentPassportPhoto
            files={files}
            setFiles={setFiles}
            applicationType={applicationType}
            draftFiles={draftFiles}
            setDraftFiles={setDraftFiles}
            uploadDocumentType={UploadDocumentType.SUPP_DOC}
            isLoading={isLoading}
            isFetchedDraft={isFetchedDraft}
            hasEditAccess={hasEditAccess}
          />
          {!isPassportDisable && (
            <>
              <Divider className={classes.divider} />
              <Typography className={classes.sectionTitle} variant="h3">
                {t('exam->>UploadDocument.myPassportTitle')}
              </Typography>
              {myPassportContentAEISSPERS(classes, t)}
            </>
          )}
        </>
      ) : (
        <>
          <SectionHeader
            className={classes.SPERSTitleContent}
            title={t('exam->>UploadDocument.UploadDocuments')}
          />
          {myPassportContentAEISSPERS(classes, t)}
        </>
      )}
    </>
  );
};

export default NonNationalUpperUploadContent;
